import { useCallback, useEffect, useRef, useState } from 'react';
import {
  Edge,
  type Node,
  type NodeChange,
  type OnNodesChange,
  applyNodeChanges,
  getConnectedEdges,
} from 'reactflow';
import * as Y from 'yjs';

// We are using nodesMap as the one source of truth for the nodes.
// This means that we are doing all changes to the nodes in the map object.
// Whenever the map changes, we update the nodes state.

const NODE_RESIZE_SYNC_INTERVAL_MS = 33;

function useNodesStateSynced(
  ydoc: Y.Doc,
): [Node[], React.Dispatch<React.SetStateAction<Node[]>>, OnNodesChange] {
  const [nodes, setNodes] = useState<Node[]>([]);
  const nodesMapRef = useRef<Y.Map<Node>>(ydoc.getMap<Node>('nodes') as Y.Map<Node>);
  const edgesMapRef = useRef<Y.Map<Edge>>(ydoc.getMap<Edge>('edges') as Y.Map<Edge>);
  const bufferedDragChangesRef = useRef<Map<string, NodeChange>>(new Map());
  const resizeThrottleRef = useRef<{
    lastWrite: number;
    timer: number | undefined;
    pending: NodeChange[] | null;
  }>({ lastWrite: 0, timer: undefined, pending: null });

  const setNodesSynced = useCallback(
    (nodesOrUpdater: React.SetStateAction<Node[]>) => {
      ydoc.transact(() => {
        const nodesMap = nodesMapRef.current;
        const seen = new Set<string>();
        const next =
          typeof nodesOrUpdater === 'function'
            ? nodesOrUpdater(Array.from(nodesMap.values())) // Convert to array here
            : nodesOrUpdater;

        const validNext = next.filter((node: Node) => node !== undefined && node !== null);

        for (const node of validNext) {
          if (node && node.id) {
            seen.add(node.id);
            nodesMap.set(node.id, node);
          }
        }

        for (const node of Array.from(nodesMap.values())) {
          if (node && node.id && !seen.has(node.id)) {
            nodesMap.delete(node.id);
          }
        }
      });
    },
    [ydoc],
  );

  const applyChangesToMap = useCallback(
    (changes: NodeChange[]) => {
      ydoc.transact(() => {
        const nodesMap = nodesMapRef.current;
        const edgesMap = edgesMapRef.current;
        const nodes = Array.from(nodesMap.values());
        const nextNodes = applyNodeChanges(changes, nodes as Node[]);

        for (const change of changes) {
          if (change.type === 'add' || change.type === 'reset') {
            nodesMap.set(change.item.id, change.item);
          } else if (change.type === 'remove' && nodesMap.has(change.id)) {
            const deletedNode = nodesMap.get(change.id);
            if (deletedNode) {
              const connectedEdges = getConnectedEdges(
                [deletedNode],
                Array.from(edgesMap.values()) as Edge[],
              );

              nodesMap.delete(change.id);

              for (const edge of connectedEdges) {
                edgesMap.delete(edge.id);
              }
            }
          } else {
            const updatedNode = nextNodes.find(n => n && n.id === change.id);
            if (updatedNode) {
              nodesMap.set(change.id, updatedNode);
            }
          }
        }
      });
    },
    [ydoc],
  );

  // Writes the latest pending resize frame (which carries the real dimensions) so the
  // final size is exact, then clears the throttle state.
  const flushResize = useCallback(() => {
    const state = resizeThrottleRef.current;
    window.clearTimeout(state.timer);
    state.timer = undefined;
    if (state.pending) {
      applyChangesToMap(state.pending);
      state.pending = null;
      state.lastWrite = Date.now();
    }
  }, [applyChangesToMap]);

  // The onNodesChange callback updates nodesMap.
  // When the changes are applied to the map, the observer will be triggered and updates the nodes state.
  // Two interactions are rate-limited to avoid a Yjs write on every pointer move:
  // - Live drag frames (position changes with dragging === true) are buffered and only
  //   flushed when the drag ends, so remote peers receive one write per drag.
  // - Live resize frames (dimensions changes with resizing === true) are throttled to
  //   ~30fps; unlike drag, the local resize is only visible after the write round-trips,
  //   so we keep writing during the resize and flush the exact final size when it ends.
  const onNodesChanges: OnNodesChange = useCallback(
    changes => {
      const isLiveResizeFrame = changes.some(
        change => change.type === 'dimensions' && change.resizing === true,
      );

      if (isLiveResizeFrame) {
        const state = resizeThrottleRef.current;
        const now = Date.now();
        const elapsed = now - state.lastWrite;
        if (elapsed >= NODE_RESIZE_SYNC_INTERVAL_MS) {
          window.clearTimeout(state.timer);
          state.timer = undefined;
          state.pending = null;
          state.lastWrite = now;
          applyChangesToMap(changes);
          return;
        }
        state.pending = changes;
        window.clearTimeout(state.timer);
        state.timer = window.setTimeout(() => {
          const pending = resizeThrottleRef.current;
          pending.timer = undefined;
          pending.lastWrite = Date.now();
          if (pending.pending) {
            applyChangesToMap(pending.pending);
            pending.pending = null;
          }
        }, NODE_RESIZE_SYNC_INTERVAL_MS - elapsed);
        return;
      }

      flushResize();

      const buffer = bufferedDragChangesRef.current;
      const pendingWrites: NodeChange[] = [];

      for (const change of changes) {
        const isLiveDragFrame = change.type === 'position' && change.dragging === true;
        if (isLiveDragFrame) {
          buffer.set(change.id, change);
        } else {
          pendingWrites.push(change);
        }
      }

      if (pendingWrites.length === 0) return;

      if (buffer.size > 0) {
        applyChangesToMap([...buffer.values(), ...pendingWrites]);
        buffer.clear();
        return;
      }

      applyChangesToMap(pendingWrites);
    },
    [applyChangesToMap, flushResize],
  );

  // here we are observing the nodesMap and updating the nodes state whenever the map changes.
  useEffect(() => {
    const nodesMap = nodesMapRef.current;

    const observer = () => {
      const allNodes = Array.from(nodesMap.values()) as Node[];
      const validNodes = allNodes.filter(node => node !== undefined && node !== null);
      setNodes(validNodes);
    };

    nodesMap.observe(observer);
    observer();

    return () => {
      nodesMap.unobserve(observer);
      window.clearTimeout(resizeThrottleRef.current.timer);
    };
  }, []);

  return [nodes, setNodesSynced, onNodesChanges];
}

export default useNodesStateSynced;
